import { QueryClientProvider } from '@tanstack/react-query';
import { LoaderCircle } from 'lucide-react';
import { type ComponentType, lazy, type ReactNode, Suspense } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';

import type { AdminCapability } from 'common/models/admin/rbac';

import { Layout } from './components/layout';
import { AuthProvider, useAuth } from './lib/auth-context';
import { queryClient } from './lib/query-client';

function lazyRoute<TModule extends Record<string, unknown>, TKey extends keyof TModule & string>(
  loader: () => Promise<TModule>,
  exportName: TKey,
) {
  return lazy(async () => ({
    default: (await loader())[exportName] as ComponentType,
  }));
}

const SignInPage = lazyRoute(() => import('./pages/sign-in'), 'SignInPage');
const GoogleLoginRedirectPage = lazyRoute(
  () => import('./pages/google-login-redirect'),
  'GoogleLoginRedirectPage',
);
const DashboardPage = lazyRoute(() => import('./pages/dashboard'), 'DashboardPage');
const AnalyticsOverviewPage = lazyRoute(() => import('./pages/analytics/overview'), 'AnalyticsOverviewPage');
const AnalyticsSessionsPage = lazyRoute(() => import('./pages/analytics/sessions'), 'AnalyticsSessionsPage');
const AnalyticsProductPage = lazyRoute(() => import('./pages/analytics/product'), 'AnalyticsProductPage');
const AnalyticsSessionAutoRefreshProvider = lazyRoute(
  () => import('./pages/analytics/session-auto-refresh'),
  'AnalyticsSessionAutoRefreshProvider',
);
const AnalyticsSessionDetailPage = lazyRoute(
  () => import('./pages/analytics/session-detail'),
  'AnalyticsSessionDetailPage',
);
const AnalyticsParticipantDetailPage = lazyRoute(
  () => import('./pages/analytics/participant-detail'),
  'AnalyticsParticipantDetailPage',
);
const OrganizationsPage = lazyRoute(() => import('./pages/organizations/index'), 'OrganizationsPage');
const OrgDetailPage = lazyRoute(() => import('./pages/organizations/org-detail'), 'OrgDetailPage');
const DeletedOrganizationsPage = lazyRoute(
  () => import('./pages/organizations/deleted-orgs'),
  'DeletedOrganizationsPage',
);
const DeletedOrgDetailPage = lazyRoute(
  () => import('./pages/organizations/deleted-org-detail'),
  'DeletedOrgDetailPage',
);
const SessionDetailPage = lazyRoute(
  () => import('./pages/organizations/session-detail'),
  'SessionDetailPage',
);
const UsersPage = lazyRoute(() => import('./pages/users/index'), 'UsersPage');
const UserDetailPage = lazyRoute(() => import('./pages/users/user-detail'), 'UserDetailPage');
const DeleteUserPage = lazyRoute(() => import('./pages/users/delete-user'), 'DeleteUserPage');
const FleetOverviewPage = lazyRoute(() => import('./pages/fleet/overview'), 'FleetOverviewPage');
const FleetPodDetailPage = lazyRoute(() => import('./pages/fleet/pod-detail'), 'FleetPodDetailPage');
const FleetTasksPage = lazyRoute(() => import('./pages/fleet/tasks'), 'FleetTasksPage');
const FleetRegionsPage = lazyRoute(() => import('./pages/fleet/regions'), 'FleetRegionsPage');
const FleetTrendsPage = lazyRoute(() => import('./pages/fleet/trends'), 'FleetTrendsPage');
const TestRunsPage = lazyRoute(() => import('./pages/test-runs'), 'TestRunsPage');

function RouteLoadingScreen() {
  return (
    <div className="flex h-screen items-center justify-center bg-slate-900">
      <div className="flex items-center gap-3 text-sm text-slate-300">
        <LoaderCircle className="h-5 w-5 animate-spin text-cyan-300" />
        Loading admin
      </div>
    </div>
  );
}

function ProtectedRoute() {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-900">
        <div className="flex items-center gap-3 text-sm text-slate-300">
          <LoaderCircle className="h-5 w-5 animate-spin text-cyan-300" />
          Loading admin
        </div>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return <Navigate to="/sign-in" replace />;
  }

  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}

function RequireCapability({ capability, children }: { capability: AdminCapability; children?: ReactNode }) {
  const { can } = useAuth();
  if (!can(capability)) return <Navigate to="/" replace />;
  // Usable as a single-element guard (children) or a pathless layout route (renders the matched
  // child via Outlet). Redirects capability-less staff to "/" (the baseline dashboard).
  return children ? children : <Outlet />;
}

export function App() {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Suspense fallback={<RouteLoadingScreen />}>
            <Routes>
              <Route path="/sign-in" element={<SignInPage />} />
              <Route
                path="/google-login-redirect/:customToken/redirectToApp/:redirectToApp"
                element={<GoogleLoginRedirectPage />}
              />
              <Route path="/google-login-redirect/:error" element={<GoogleLoginRedirectPage />} />
              <Route element={<ProtectedRoute />}>
                <Route path="/" element={<DashboardPage />} />
                <Route element={<RequireCapability capability="analytics:view" />}>
                  <Route path="/analytics" element={<AnalyticsOverviewPage />} />
                  <Route path="/analytics/sessions" element={<AnalyticsSessionsPage />} />
                  <Route path="/analytics/product" element={<AnalyticsProductPage />} />
                  <Route
                    path="/analytics/sessions/:sessionId"
                    element={<AnalyticsSessionAutoRefreshProvider />}
                  >
                    <Route index element={<AnalyticsSessionDetailPage />} />
                    <Route path="participants/:peerId" element={<AnalyticsParticipantDetailPage />} />
                  </Route>
                </Route>
                <Route element={<RequireCapability capability="fleet:view" />}>
                  <Route path="/fleet" element={<FleetOverviewPage />} />
                  <Route path="/fleet/tasks" element={<FleetTasksPage />} />
                  <Route path="/fleet/regions" element={<FleetRegionsPage />} />
                  <Route path="/fleet/trends" element={<FleetTrendsPage />} />
                  <Route path="/fleet/pods/:podId" element={<FleetPodDetailPage />} />
                </Route>
                <Route element={<RequireCapability capability="test-runs:view" />}>
                  <Route path="/test-runs" element={<TestRunsPage />} />
                </Route>
                <Route element={<RequireCapability capability="org:view" />}>
                  <Route path="/organizations" element={<OrganizationsPage />} />
                  <Route path="/organizations/deleted" element={<DeletedOrganizationsPage />} />
                  <Route path="/organizations/deleted/:orgId" element={<DeletedOrgDetailPage />} />
                  <Route path="/organizations/:orgId" element={<OrgDetailPage />} />
                  <Route path="/organizations/:orgId/sessions/:sessionId" element={<SessionDetailPage />} />
                </Route>
                <Route element={<RequireCapability capability="user:view" />}>
                  <Route path="/users" element={<UsersPage />} />
                  <Route path="/users/:userId" element={<UserDetailPage />} />
                </Route>
                <Route element={<RequireCapability capability="user:delete" />}>
                  {/* Static segment outranks /users/:userId in React Router, so this resolves first. */}
                  <Route path="/users/delete" element={<DeleteUserPage />} />
                </Route>
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}
